// What the composer says after the server has answered a send.
//
// ⚠️ A 200 IS NOT "EVERYONE WAS MAILED", and this is the rule that keeps that from being
// silent. `filterAndSendMessageToCandidatesByIds` filters the id list server-side and answers
// with the split — `sentIds` and `blacklistedIds` — so a recruiter who selected twelve and
// sees no message will assume twelve were contacted.
//
// Upstream says it in a modal: `showMessageSentDialogue(candidateAmount, blacklistedAmount)`
// (`shortlist.candidate-message.service.ng1.ts:106`) builds two sentences, one of them only
// when something was filtered, and auto-closes. That dialogue is a template and a service this
// port does not have yet; the sentences are ported here so that when it lands it has the copy,
// and so the screen can say it in the meantime.
/**
 * `getEntityNameText()` — "candidate" on the candidates composer, "contact" on the contacts
 * one (`extension-alert-client-contacts-messages.component.ng1.ts:183`). Upstream's words, and
 * note that the contacts one is NOT "client contact" here.
 */
export type MessageEntityNoun = 'candidate' | 'contact'

export interface MessageSendSplit {
  sentIds?: number[]
  blacklistedIds?: number[]
}

export type MessageSentOutcome =
  /** Nothing was filtered — upstream's "Great! …" case. Close, as it does. */
  | { close: true; message: string }
  /** Some were filtered. Stay open and say so; closing would take the sentence with it. */
  | { close: false; message: string }

/**
 * `showMessageSentDialogue`'s two lines, ported.
 *
 * ⚠️ THE COUNT IN THE SENTENCE IS THE SENT COUNT, NOT THE SELECTED ONE. Upstream passes
 * `candidates.length` — the list it actually sent — so "We will send your message to 9
 * candidates" after twelve were selected is correct and is the point: the other three are the
 * blacklisted line above it.
 *
 * ⚠️ AND "Great! " IS CONDITIONAL. It prefixes the sentence only when nothing was filtered,
 * which is upstream's own expression and the reason the two lines are built together rather
 * than independently.
 */
export function messageSentOutcome(
  response: MessageSendSplit | undefined,
  noun: MessageEntityNoun = 'candidate',
): MessageSentOutcome {
  const sent = response?.sentIds?.length ?? 0
  const blacklisted = response?.blacklistedIds?.length ?? 0
  const plural = (count: number, word: string): string => (count === 1 ? word : `${word}s`)
  const sentLine = `${blacklisted === 0 ? 'Great! ' : ''}We will send your message to ${String(sent)} ${plural(sent, noun)}.`

  if (blacklisted === 0) return { close: true, message: sentLine }

  // `${n} ${n > 1 ? 'candidates are' : 'candidate is'} blacklisted and not allowed to be sent.`
  const blacklistedLine =
    `${String(blacklisted)} ${plural(blacklisted, noun)} ` +
    `${blacklisted === 1 ? 'is' : 'are'} blacklisted and not allowed to be sent.`
  return { close: false, message: `${blacklistedLine} ${sentLine}` }
}
